import React, { useRef, useState, useEffect } from "react";
import BlogCard from "./BlogCard";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";

interface Blog {
    image: string;
    title: string;
    description: string;
    tags: string[];
    published_date: string;
    read_time: string;
}

interface BlogSliderProps {
    blogs: Blog[];
}

const BlogSlider: React.FC<BlogSliderProps> = ({ blogs }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const itemsPerView = 3; // number of visible cards at once

    const totalSlides = Math.ceil(blogs.length / itemsPerView);

    const scrollToIndex = (index: number) => {
        if (scrollRef.current) {
            const cardWidth = scrollRef.current.offsetWidth / itemsPerView;
            scrollRef.current.scrollTo({
                left: cardWidth * itemsPerView * index,
                behavior: "smooth",
            });
            setActiveIndex(index);
        }
    };

    const scroll = (direction: "left" | "right") => {
        if (direction === "left") {
            const newIndex = Math.max(activeIndex - 1, 0);
            scrollToIndex(newIndex);
        } else {
            const newIndex = Math.min(activeIndex + 1, totalSlides - 1);
            scrollToIndex(newIndex);
        }
    };

    return (
        <section className="py-10 px-4 ">
            {/* Header */}
            <div className="flex justify-between flex-col md:flex-row mb-10">
                <div>
                    <button className="px-3 py-1 border text-xs rounded-full mb-2">
                        View all blogs
                    </button>
                    <h2 className="text-4xl font-bold mb-2">Blog and Article</h2>
                    <p className="text-gray-600 max-w-xl">
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit. Sed ut
                        perspiciatis unde omnis iste natus error sit voluptatem accusantium.
                    </p>
                </div>
                <a
                    href="#"
                    className="text-green-600 mt-4 md:mt-0 text-sm hover:underline"
                >
                    Learn more →
                </a>
            </div>

            {/* Slider */}
            <div className="overflow-hidden">
                <div
                    ref={scrollRef}
                    className="flex gap-6 overflow-x-auto scroll-smooth scrollbar-hide"
                >
                    {blogs.map((blog, index) => (
                        <div
                            key={index}
                            className="min-w-[400px] max-w-[500px] flex-shrink-0"
                        >
                            <BlogCard {...blog} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer: dots + arrows */}
            <div className="flex justify-between items-center mt-6 px-2">
                {/* Dots */}
                {/* Dots - Limited to 7 max, with active slide centered */}
                <div className="flex gap-2">
                    {Array.from({ length: totalSlides }).map((_, index) => {
                        // Only show max 7 dots (center activeIndex if possible)
                        const isVisible = Math.abs(index - activeIndex) <= 3 || index === 0 || index === totalSlides - 1;

                        return isVisible ? (
                            <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${index === activeIndex ? "bg-gray-800 scale-110" : "bg-gray-400"
                                    }`}
                            ></div>
                        ) : (
                            // Show ellipsis (once) when skipping hidden dots
                            (index === activeIndex - 4 || index === activeIndex + 4) && (
                                <span key={`ellipsis-${index}`} className="text-gray-400 text-sm">• • •</span>
                            )
                        );
                    })}
                </div>


                {/* Arrows */}
                <div className="flex gap-2">
                    <button
                        onClick={() => scroll("left")}
                        className="text-sm p-2 rounded-full hover:bg-gray-100"
                    >
                        <FiArrowLeft />
                    </button>
                    <button
                        onClick={() => scroll("right")}
                        className="text-sm p-2 rounded-full hover:bg-gray-100"
                    >
                        <FiArrowRight />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default BlogSlider;
